import React, { useState } from 'react';
import { useMediaQuery } from '@material-ui/core';
import './styles.scss';
import { TrackingDetailsProps } from './types';
import AloStaticSnackbar from 'src/scripts/templates/_shared/ui-components/YogaUI/alo-ui/AloStaticSnackbar';
import ProgressBar from 'src/scripts/templates/_shared/ui-components/ProgressBar';
import TabButtons, { TabItem } from 'src/scripts/templates/_shared/ui-components/TabButtons';
import { formatDeliveredTimestamp, formatTimeRange, getOrderUrl } from './utils';
import { MUI_MOBILE_BREAKPOINT } from 'src/scripts/utils/constants';
import { prependLocaleToPath } from 'src/scripts/utils/localization';

const TrackingDetails = ({
  orderNumber = '',
  orderReference,
  orderStatusUrl = '',
  shipments = [],
  authParam,
  activeShipmentTabIndex,
  onTabChange,
  isError = false,
}: TrackingDetailsProps) => {
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });

  const activeShipment = shipments[activeShipmentTabIndex];

  const {
    status_message: statusMessage,
    checkpoints,
    courier_image_url: courierImageUrl,
    tracking_company: trackingCompany,
    tracking_url: trackingUrl,
    tracking_number: trackingNumber,
  } = activeShipment || {};

  const {
    title: statusTitle,
    heading: statusHeading,
    description: statusDescriptionHtml,
    completion_percentage: completionPercentage,
    delivered_at: deliveredAt,
    time_range: timeRange,
  } = statusMessage || {};

  const shouldShowError = isError || !statusTitle;

  const progressPercentage = completionPercentage || 25;

  const subtitleHtml =
    statusHeading?.toLowerCase() === 'delivered' && deliveredAt
      ? formatDeliveredTimestamp(deliveredAt)
      : timeRange
      ? formatTimeRange(timeRange)
      : '';

  const orderUrl = getOrderUrl(orderStatusUrl, orderReference, authParam);

  const tabItems: TabItem[] = shipments.map((shipment, index) => ({
    key: shipment.fulfillment_id,
    title: `Shipment ${index + 1}`,
  }));

  const formattedCheckpoints =
    checkpoints?.map((checkpoint) => {
      const checkpointDate = new Date(checkpoint.checkpoint_time);
      const formattedDate = checkpointDate.toLocaleDateString(navigator.language, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
      const formattedTime = checkpointDate.toLocaleTimeString(navigator.language, {
        hour: '2-digit',
        minute: '2-digit',
      });

      return {
        ...checkpoint,
        formattedDate,
        formattedTime,
      };
    }) || [];

  return (
    <div className="p-xl">
      <p className="body-small d-flex justify-content-center">
        Order number:
        <a href={prependLocaleToPath(orderUrl)} className="body-text-link-small ml-1">
          {orderNumber}
        </a>
      </p>

      <div className="mt-m-sm">
        <div className="tabs-scroll-wrapper">
          {shipments.length > 1 && (
            <TabButtons
              tabs={tabItems}
              activeIndex={activeShipmentTabIndex}
              onTabChange={onTabChange}
              buttonSize={isMobile ? 'xs' : 'sm'}
              wrapperClassName="tabs-list"
            />
          )}
        </div>
      </div>

      {!shouldShowError && statusTitle && (
        <div className="my-3xl d-flex flex-column align-items-center text-center">
          {statusHeading && <p className="body-large">{statusHeading}</p>}
          {statusTitle && <h3 className="my-md">{statusTitle}</h3>}
          {subtitleHtml && <p className="body-large" dangerouslySetInnerHTML={{ __html: subtitleHtml }} />}
        </div>
      )}

      {shouldShowError && (
        <div className="mt-lg">
          <AloStaticSnackbar message="Unable to load tracking history. Check back soon." type="error" />
        </div>
      )}

      <div className="my-3xl d-flex flex-column align-items-center">
        {!shouldShowError && statusDescriptionHtml && (
          <p className="body-large">
            <span className="body semibold" dangerouslySetInnerHTML={{ __html: statusDescriptionHtml }} />
          </p>
        )}
        <div className="progress-bar-container mt-lg">
          <ProgressBar percentage={shouldShowError ? 20 : progressPercentage} />
        </div>
      </div>

      {!shouldShowError && formattedCheckpoints?.length > 0 && (
        <div>
          <button
            type="button"
            className="body-text-link-small d-flex history-toggle"
            onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
          >
            {isHistoryExpanded ? 'Hide Full Tracking History' : 'Show Full Tracking History'}
            <span className={`alo-chevron-${isHistoryExpanded ? 'up' : 'down'}-icon ml-2`}></span>
          </button>
          {isHistoryExpanded && (
            <>
              <div className="mt-lg mb-lg">
                <AloStaticSnackbar
                  message="You’re viewing the most up-to-date tracking details available, the same information our team sees."
                  type="info"
                />
              </div>
              <ul>
                {formattedCheckpoints.map((checkpoint) => (
                  <li className="history-item my-md d-flex" key={`${checkpoint.checkpoint_time}-${checkpoint.message}`}>
                    <div>
                      <p className="body semibold">{checkpoint.formattedDate}</p>
                      <p className="body">{checkpoint.formattedTime}</p>
                    </div>
                    <div className="ml-xl">
                      <p className="body semibold">{checkpoint.message}</p>
                      <p className="body">{checkpoint.location}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {activeShipment && (
        <div className="tracking-number-section d-flex align-items-center mt-3xl">
          <div className="carrier-logo mr-xl">
            <img src={courierImageUrl} alt={trackingCompany || 'courier-logo'} />
          </div>
          <div className="tracking-number-info">
            <p className="body semibold">Tracking Number</p>
            <a href={trackingUrl} target="_blank" rel="noopener noreferrer" className="body-text-link-small">
              {trackingNumber}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackingDetails;
